import { useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { CircleX } from "lucide-react";
import { Button, RadioButton } from "@/components/atoms";
import type { RecurrenceScope } from "@/types";
import "../Modal/Modal.scss";
import "./RecurrenceScopeDialog.scss";

interface RecurrenceScopeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: "edit" | "delete";
  onConfirm: (scope: RecurrenceScope) => void;
}

const SCOPE_OPTIONS: Record<string, string> = {
  THIS: "Solo este evento",
  FOLLOWING: "Este y los siguientes",
  ALL: "Toda la serie",
};

/**
 * Dialog for choosing the scope of an edit or delete action on a recurring event series.
 */
export function RecurrenceScopeDialog({
  open,
  onOpenChange,
  action,
  onConfirm,
}: RecurrenceScopeDialogProps) {
  const [scope, setScope] = useState<RecurrenceScope>("THIS");

  const handleConfirm = (): void => {
    onConfirm(scope);
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      setScope("THIS");
    }
    onOpenChange(nextOpen);
  };

  const title =
    action === "delete"
      ? "Eliminar evento recurrente"
      : "Editar evento recurrente";
  const confirmLabel = action === "delete" ? "Eliminar" : "Guardar";
  const confirmVariant = action === "delete" ? "danger" : "primary";

  return (
    <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="modal__overlay" />
        <AlertDialog.Content className="modal__container">
          <div className="modal__header">
            <AlertDialog.Title className="modal__title">{title}</AlertDialog.Title>
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="modal__close-button"
                aria-label="Cerrar"
              >
                <CircleX />
              </button>
            </AlertDialog.Cancel>
          </div>
          <div className="modal__content">
            <AlertDialog.Description className="recurrence-scope-dialog__description">
              Este elemento es parte de una serie de eventos que se repiten. ¿A
              cuáles eventos de la repetición quieres aplicar los cambios?
            </AlertDialog.Description>

            <div className="recurrence-scope-dialog__scope-selector">
              <RadioButton
                options={SCOPE_OPTIONS}
                value={scope}
                onChange={(v) => setScope(v as RecurrenceScope)}
                variant="secondary"
                density="default"
              />
            </div>

            <div className="recurrence-scope-dialog__actions">
              <AlertDialog.Cancel asChild>
                <Button
                  variant="secondary"
                  motionScale="base"
                  actionTrackSufix="bo_scope_cancel"
                >
                  Cancelar
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  variant={confirmVariant}
                  motionScale="base"
                  actionTrackSufix="bo_scope_confirm"
                  onClick={handleConfirm}
                >
                  {confirmLabel}
                </Button>
              </AlertDialog.Action>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
